// Package 2 · Item 3 (Feb 2026) — Sidebar red-dot warnings discoverability.
// ---------------------------------------------------------------------
// Layout.jsx renders a small red dot on the Schedule nav item whenever
// the currently-viewed day carries at least one UNACKNOWLEDGED warning
// (see `_decorate_warning_acknowledgements` on the backend). Purpose:
// alert admins to hidden warnings when they're not on the Schedule
// page, so acknowledgement never gets silently lost.
//
// The "currently-viewed day" resolves as follows:
//   1. If the user is on `/schedule`, we honor the `?date=` query.
//   2. Otherwise we fall back to today's date (client TZ).
// This mirrors `scheduleDateFromQuery`'s semantics so the sidebar's
// signal aligns with what the Schedule board would render on click.
//
// Data source: `GET /api/session-warning-acknowledgements/summary`
// returns `{unacked_count: int}` for a single date. Cheap (< 200 ms
// on preview, 19-byte response). TanStack-Query-cached with a 30 s
// stale window so the badge doesn't spam the endpoint.
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { scheduleDateFromQuery } from "@/lib/scheduleDateFromQuery";

export function useScheduleWarningBadge({ enabled = true } = {}) {
  const location = useLocation();
  const date = location.pathname.startsWith("/schedule")
    ? scheduleDateFromQuery(location.search)
    : new Date().toISOString().slice(0, 10);

  const query = useQuery({
    queryKey: ["sched", "warnings-summary", date],
    queryFn: async () => {
      const r = await api.get(
        `/session-warning-acknowledgements/summary`,
        { params: { date } },
      );
      return r.data;
    },
    enabled,
    staleTime: 30_000,
    // Auto-refresh every 60s so an ack elsewhere clears the badge without
    // requiring the user to navigate.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const unackedCount = query.data?.unacked_count ?? 0;
  return {
    date,
    unackedCount,
    hasUnacked: unackedCount > 0,
    isLoading: query.isLoading,
  };
}
